import { requestJSON, type AuthSession, type IDSOperation } from "./api";

export interface EmailConfiguration {
 revision: number;
 runtime_active: boolean;
 data_center_id: string;
 smtp: { enabled: boolean; host: string; port: number; username: string; from_address: string; from_name: string; security: "none" | "ssl" | "tls"; password_configured: boolean; password?: string; clear_password?: boolean };
}
export interface ArtifactConfiguration {
 revision: number; runtime_active: boolean; data_center_id: string; backend: "filesystem" | "s3" | "s3-compatible";
 max_artifact_size_mb: number; retention_days: number;
 filesystem: { root: string };
 s3: { bucket: string; region: string; endpoint: string; prefix: string; force_path_style: boolean; credential_mode: "env" | "irsa" | "access_key"; access_key_configured: boolean; secret_access_key_configured: boolean; access_key_id?: string; secret_access_key?: string; clear_secret?: boolean };
}
export interface EmailLog {id: string; team_id: string; recipient: string; subject: string; purpose: string; status: string; delivery_mode: string; error_message: string; error_code?: string; smtp_code?: number; created_at_unix: number; body_text?: string; body_html?: string;}
export interface EmailLogPage {items: EmailLog[]; next_cursor: string}
export interface WebSearchConfiguration {
 revision: number; runtime_active: boolean; data_center_id: string;
 provider: "tavily"; enabled: boolean; timeout_seconds: number;
 api_key_configured: boolean; api_key?: string; clear_api_key?: boolean;
}
export interface WebSearchTestResult {ok: boolean; provider: "tavily"; revision: number; latency_ms: number; result_count: number}
export interface ImageGenerationModel {
 ref: string; provider: "tencent-hy-image-3.5"; display_name: string; enabled: boolean;
 token_configured: boolean; token?: string; clear_token?: boolean; created_at_ms: number;
 prices_usd: {"1k": string; "2k": string; "4k": string};
}
export interface ImageGenerationConfiguration {
 revision: number; runtime_active: boolean; data_center_id: string;
 default_model_ref: string; models: ImageGenerationModel[];
}
const prefix = "/api/v1/platform-services";
export function readPlatformConfiguration<T>(service: "email" | "artifact-storage" | "web-search" | "image-generation", signal?: AbortSignal) { return requestJSON<T>(`${prefix}/${service}/config`, {signal}); }
export function platformCommand<T>(service: "email" | "artifact-storage" | "web-search" | "image-generation", action: "config" | "test" | "upload-test", body: {expected_revision: number; config?: unknown; recipient?: string; test_file?: {name: string; content_type: string; content_base64: string}}, session: AuthSession, reason: string) { const clean = {...body};
 if (body.config && typeof body.config === "object") {
  if (service === "email") {
   const {password_configured: _configured, ...smtp} = (body.config as EmailConfiguration).smtp;
   clean.config = {smtp};
  } else if (service === "web-search") {
   const source = body.config as WebSearchConfiguration;
   clean.config = {provider: source.provider, enabled: source.enabled, timeout_seconds: source.timeout_seconds, api_key: source.api_key, clear_api_key: source.clear_api_key};
  } else if (service === "image-generation") {
   const source = body.config as ImageGenerationConfiguration;
   clean.config = {default_model_ref: source.default_model_ref, models: source.models.map(model => {
    const {token_configured: _configured, ...value} = model;
    return value;
   })};
  } else {
   const source = body.config as ArtifactConfiguration;
   const {access_key_configured: _accessConfigured, secret_access_key_configured: _secretConfigured, ...s3} = source.s3;
   clean.config = {backend: source.backend, max_artifact_size_mb: source.max_artifact_size_mb, retention_days: source.retention_days, filesystem: source.filesystem, s3};
  }
 }
 return requestJSON<T>(`${prefix}/${service}/${action}`, {method: action === "config" ? "PUT" : "POST", body: clean, csrfToken: session.csrf_token, reason}); }
export function readEmailLogs(before = "", signal?: AbortSignal) { return requestJSON<EmailLogPage>(`${prefix}/email/logs?limit=50&before=${encodeURIComponent(before)}`, {signal}); }
export function readEmailLog(id: string) { return requestJSON<EmailLog>(`${prefix}/email/logs/${encodeURIComponent(id)}`); }
export function readPlatformOperations(signal?: AbortSignal) { return requestJSON<{items: IDSOperation[]}>(`${prefix}/operations`, {signal}); }

export interface IdentityMailStatus {
 unavailable_reason?: string;
 revision: number; source_data_center_id: string; source_revision: number;
 updated_by: string; updated_at: string; configured: boolean; writable: boolean;
 connection_verified: boolean; delivery_verified: boolean;
}
export function readIdentityMail(signal?: AbortSignal) {
 return requestJSON<IdentityMailStatus>(`${prefix}/email/identity-sync`, {signal});
}
export function syncIdentityMail(session: AuthSession, sourceRevision: number, expectedRevision: number, replaceExisting: boolean, reason: string) {
 return requestJSON<IdentityMailStatus>(`${prefix}/email/identity-sync`, {method:"POST", csrfToken:session.csrf_token, reason,
  body:{source_revision:sourceRevision, expected_revision:expectedRevision, replace_existing:replaceExisting}});
}
